<script lang="ts">
    import { page } from '@inertiajs/svelte';

    interface LeaderboardEntry {
        user: { id: number | null; name: string | null };
        correct_count: number;
    }

    interface CurrentUserRank {
        rank: number;
        correct_count: number;
    }

    let { entries = [], tiedOverflow = 0, currentUserRank = null }: { entries: LeaderboardEntry[]; tiedOverflow: number; currentUserRank: CurrentUserRank | null } = $props();

    let currentUser = $derived(($page as any).props?.auth?.user as { id: number; name: string } | undefined);
    let currentUserId = $derived(currentUser?.id);

    let mobileExpanded = $state(false);
    let isLg = $state(false);

    $effect(() => {
        const mq = window.matchMedia('(min-width: 1024px)');
        isLg = mq.matches;
        const handler = (e: MediaQueryListEvent) => isLg = e.matches;
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    });

    let showAll = $derived(isLg || mobileExpanded || entries.length <= 1);

    function getRank(index: number): number {
        if (index === 0) return 1;
        if (entries[index].correct_count === entries[index - 1].correct_count) {
            return getRank(index - 1);
        }
        return index + 1;
    }
</script>

<div class="bg-gray-900 rounded-lg shadow border border-gray-700 p-6">
    <h2 class="text-lg font-semibold mb-4 flex items-center gap-2">
        <svg class="size-5 text-cyan-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512"><path opacity=".4" fill="currentColor" d="M0 368l0 96c0 26.5 21.5 48 48 48l64 0c26.5 0 48-21.5 48-48l0-96c0-26.5-21.5-48-48-48l-64 0c-26.5 0-48 21.5-48 48zm208-64l0 160c0 26.5 21.5 48 48 48l64 0c26.5 0 48-21.5 48-48l0-160c0-26.5-21.5-48-48-48l-64 0c-26.5 0-48 21.5-48 48zM416 432l0 32c0 26.5 21.5 48 48 48l64 0c26.5 0 48-21.5 48-48l0-32c0-26.5-21.5-48-48-48l-64 0c-26.5 0-48 21.5-48 48z"/><path fill="currentColor" d="M298.2 6.3l23.6 47.8 52.3 7.5c9.3 1.4 13.2 12.9 6.4 19.8l-38 36.6 9 52.1c1.4 9.3-8.2 16.5-16.8 12.2l-46.6-24.4-46.9 24.8c-8.6 4.3-18.3-2.9-16.8-12.2l9-52.1-38-37c-6.8-6.8-2.9-18.3 6.4-19.8l52.3-7.5 23.6-47.8c4.3-8.6 16.5-8.3 20.4 0z"/></svg>
        Leaderboard
    </h2>
    {#if entries.length === 0}
        <p class="text-gray-500 text-sm">No scores yet.</p>
    {:else}
        <ul class="divide-y divide-gray-800">
            {#each showAll ? entries : entries.slice(0, 1) as entry, i (i)}
                {@const rank = getRank(i)}
                <li class="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                    <span class="w-6 flex justify-center shrink-0">
                        {#if rank === 1}
                            <svg class="size-5 text-amber-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path opacity=".4" fill="currentColor" d="M1.1 113.7C-.8 85.6 22.1 64 48.3 64l49.6 0c.9 16.9 2.1 32.9 3.6 48l-52.4 0c6.2 84.7 45.1 127.1 85.2 149.6 10.6 27.3 22.5 48.5 34.6 65-18.4-4.8-38.4-12.1-58-23.1-54.1-30.3-102.9-87.4-109.9-189.9zm345 209.5c12-17 23.7-38.6 33.9-66.3 40.5-23.8 77.1-66.1 83.3-144.8L411 112c1.5-15.1 2.8-31.1 3.6-48l49.6 0c26.1 0 49.1 21.6 47.1 49.8-7.5 103.7-60.5 160.7-118 190.5-15.8 8.2-31.9 14.3-47.2 18.8z"/><path fill="currentColor" d="M144.3 0l224 0c26.5 0 48.1 21.8 47.1 48.2-9 234.9-83.3 305.5-127.1 326.7l0 73.1 64 0c17.7 0 32 14.3 32 32s-14.3 32-32 32l-192 0c-17.7 0-32-14.3-32-32s14.3-32 32-32l64 0 0-73.1C180.4 353.7 106.1 283.2 97.2 48.2 96.1 21.8 117.8 0 144.3 0z"/></svg>
                        {:else if rank === 2}
                            <svg class="size-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path opacity=".4" fill="currentColor" d="M11 53.5L80.6 192.6c35.2-39.7 86.5-64.6 143.7-64.6-28.2-47-56.4-94-84.6-140.9-6.5-10.8-20.1-14.7-31.3-9.1L21.8 21.3C9.9 27.2 5.1 41.6 11 53.5zM224.3 128c57.2 0 108.5 25 143.7 64.6L437.6 53.5c5.9-11.9 1.1-26.3-10.7-32.2L340.2-22.1c-11.2-5.6-24.9-1.6-31.3 9.1-28.2 47-56.4 94-84.6 140.9z"/><path fill="currentColor" d="M224.3 512a192 192 0 1 1 0-384 192 192 0 1 1 0 384zm30.8-241.5l-22.4-45.4c-3.4-7-13.3-7-16.8 0l-22.4 45.4c-1.4 2.8-4 4.7-7 5.1l-50.1 7.3c-7.7 1.1-10.7 10.5-5.2 16l36.3 35.4c2.2 2.2 3.2 5.2 2.7 8.3l-8.6 49.9c-1.3 7.6 6.7 13.5 13.6 9.9L220 378.7c2.7-1.4 6-1.4 8.7 0l44.8 23.6c6.9 3.6 14.9-2.2 13.6-9.9l-8.6-49.9c-.5-3 .5-6.1 2.7-8.3l36.3-35.4c5.6-5.4 2.5-14.8-5.2-16l-50.1-7.3c-3-.4-5.7-2.4-7-5.1z"/></svg>
                        {:else if rank === 3}
                            <svg class="size-5 text-amber-700" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path opacity=".4" fill="currentColor" d="M11 53.5L80.6 192.6c35.2-39.7 86.5-64.6 143.7-64.6-28.2-47-56.4-94-84.6-140.9-6.5-10.8-20.1-14.7-31.3-9.1L21.8 21.3C9.9 27.2 5.1 41.6 11 53.5zM224.3 128c57.2 0 108.5 25 143.7 64.6L437.6 53.5c5.9-11.9 1.1-26.3-10.7-32.2L340.2-22.1c-11.2-5.6-24.9-1.6-31.3 9.1-28.2 47-56.4 94-84.6 140.9z"/><path fill="currentColor" d="M224.3 512a192 192 0 1 1 0-384 192 192 0 1 1 0 384zm30.8-241.5l-22.4-45.4c-3.4-7-13.3-7-16.8 0l-22.4 45.4c-1.4 2.8-4 4.7-7 5.1l-50.1 7.3c-7.7 1.1-10.7 10.5-5.2 16l36.3 35.4c2.2 2.2 3.2 5.2 2.7 8.3l-8.6 49.9c-1.3 7.6 6.7 13.5 13.6 9.9L220 378.7c2.7-1.4 6-1.4 8.7 0l44.8 23.6c6.9 3.6 14.9-2.2 13.6-9.9l-8.6-49.9c-.5-3 .5-6.1 2.7-8.3l36.3-35.4c5.6-5.4 2.5-14.8-5.2-16l-50.1-7.3c-3-.4-5.7-2.4-7-5.1z"/></svg>
                        {:else}
                            <span class="text-xs font-medium text-gray-400">{rank}</span>
                        {/if}
                    </span>
                    <span class="flex-1 flex items-center gap-1.5 min-w-0">
                        {#if !entry.user.name}
                            <span class="text-xs text-gray-400 tracking-wide font-medium uppercase truncate">Anonymous</span>
                        {:else}
                            <span class="text-sm font-medium text-gray-100 truncate">{entry.user.name}</span>
                            {#if entry.user.id === currentUserId}
                                <span class="text-[10px] font-bold uppercase tracking-wide text-emerald-400 bg-emerald-950 rounded-full px-1.5 py-0.5 shrink-0">YOU</span>
                            {/if}
                        {/if}
                    </span>
                    <span class="text-xs font-semibold text-cyan-400 bg-cyan-950 rounded-full px-2.5 py-0.5 shrink-0">{entry.correct_count}</span>
                </li>
            {/each}
        </ul>
        {#if showAll}
            {#if tiedOverflow > 0}
                <p class="text-xs text-gray-400 text-center mt-3">+{tiedOverflow} more tied</p>
            {/if}
            {#if currentUserRank}
                <div class="border-t border-dashed border-gray-700 mt-3 pt-3">
                    <div class="flex items-center gap-3">
                        <span class="w-6 flex justify-center shrink-0">
                            <span class="text-xs font-medium text-gray-400">{currentUserRank.rank}</span>
                        </span>
                        <span class="flex-1 flex items-center gap-1.5 min-w-0">
                            <span class="text-sm font-medium text-gray-100 truncate">{currentUser?.name}</span>
                            <span class="text-[10px] font-bold uppercase tracking-wide text-emerald-400 bg-emerald-950 rounded-full px-1.5 py-0.5 shrink-0">YOU</span>
                        </span>
                        <span class="text-xs font-semibold text-cyan-400 bg-cyan-950 rounded-full px-2.5 py-0.5 shrink-0">{currentUserRank.correct_count}</span>
                    </div>
                </div>
            {/if}
        {/if}
        {#if !isLg && entries.length > 1}
            <button onclick={() => mobileExpanded = !mobileExpanded} class="flex items-center justify-center w-full mt-3 text-gray-400 hover:text-gray-600 transition-colors">
                <svg class="size-4 transition-transform {mobileExpanded ? 'rotate-180' : ''}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="currentColor" d="M233.4 406.6c12.5 12.5 32.8 12.5 45.3 0l192-192c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L256 338.7 86.6 169.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l192 192z"/></svg>
            </button>
        {/if}
    {/if}
</div>
